import { authClient } from "@/lib/auth-client";
import { formatDateOnly, parseDateOnly } from "@/lib/date-only";
import { PickerSearch, matchesQuery } from "@/components/admin/picker-search";
import { useState, useRef } from "react";
import { createFileRoute, useSearch, useNavigate, Link } from "@tanstack/react-router";
import { SubCredentialsList } from "@/components/subs/sub-credentials-list";
import { SubCertHolders } from "@/components/subs/sub-cert-holders";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { apiGet, apiPost, apiPatch, apiDelete, apiUrl } from "@/lib/api";
import { deleteWithUndo } from "@/lib/delete-with-undo";
import { useFileUpload } from "@/hooks/use-file-upload";
import { useUserFilter, userFilterSearch } from "@/lib/user-filter";
import { UserFilterChip } from "@/components/admin/user-filter-chip";
import { CreateCertAssignmentSheet } from "@/routes/_authenticated/admin/assignments";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import JSZip from "jszip";
import * as XLSX from "xlsx-js-style";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Award,
  CheckCircle,
  AlertTriangle,
  XCircle,
  FileText,
  Upload,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ShieldAlert,
  Search,
  Users,
  Eye,
  RefreshCw,
  Download,
  ArrowLeft,
  Clock,
  StickyNote,
  RotateCcw,
  ChevronDown,
  Check,
} from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { CertValidityDialog } from "@/components/admin/cert-validity-dialog";

export const Route = createFileRoute("/_authenticated/admin/certifications")({
  component: AdminCertificationsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === "string" ? search.q : undefined,
    assignmentId: typeof search.assignmentId === "string" ? search.assignmentId : undefined,
    workerId: typeof search.workerId === "string" ? search.workerId : undefined,
    ...(search.assign === "1" ? { assign: "1" as const } : {}),
    ...userFilterSearch(search),
  }),
});

// ─── Types ──────────────��────────────────────────────────────────────

interface CertType {
  id: string;
  name: string;
  description: string | null;
  defaultValidityMonths: number | null;
  previousValidityMonths?: number | null;
  isRequired: boolean;
  isActive: boolean;
  sortOrder: number;
  category: "trade" | "safety" | "training";
  requiresExpiry: boolean;
  requiresFile: boolean;
}

interface ComplianceEntry {
  groupKey: string;
  certTypeName: string;
  certTypeId: string | null;
  /** 1 for credentials with no standard type (the worker typed the name). */
  isCustom?: number;
  isRequired: boolean;
  total: number;
  valid: number;
  expiringSoon: number;
  expired: number;
  revoked: number;
}

interface WorkerCert {
  id: string;
  userId: string;
  userName: string | null;
  certTypeId: string;
  certTypeName: string | null;
  customCertName: string | null;
  reviewStatus: "approved" | "pending" | "rejected";
  certNumber: string | null;
  issuedDate: string | null;
  expiryDate: string | null;
  status: "valid" | "expiring_soon" | "expired" | "revoked";
  divisionId: string | null;
  divisionName: string | null;
  fileId: string | null;
  fileId2: string | null;
  notes: string | null;
  retainUntil: string | null;
  /** 1 when this expired record has been replaced by a current one (archived). */
  superseded?: number | boolean;
}

interface OrgUser {
  id: string;
  name: string | null;
  email: string;
  role: string;
  divisionId: string | null;
}

interface Division {
  id: string;
  name: string;
}

const BASE_URL = import.meta.env.VITE_API_URL || "";

const CATEGORY_LABELS: Record<string, string> = {
  trade: "Trade Tickets",
  safety: "Safety Certifications",
  training: "Training Records",
};

function groupCertTypesByCategory(types: CertType[]) {
  const order = ["trade", "safety", "training"];
  const grouped: Record<string, CertType[]> = {};
  for (const t of types) {
    const cat = t.category ?? "safety";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(t);
  }
  for (const cat of Object.keys(grouped)) {
    grouped[cat].sort((a, b) => a.name.localeCompare(b.name));
  }
  return order.filter((c) => grouped[c]?.length).map((c) => ({ category: c, types: grouped[c] }));
}

const STATUS_CONFIG = {
  valid: { label: "Valid", className: "bg-green-500/15 text-green-700 dark:text-green-400", icon: CheckCircle },
  expiring_soon: { label: "Expiring", className: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400", icon: AlertTriangle },
  expired: { label: "Expired", className: "bg-red-500/15 text-red-700 dark:text-red-400", icon: XCircle },
  revoked: { label: "Revoked", className: "bg-gray-500/15 text-gray-600 dark:text-gray-400", icon: XCircle },
} as const;

/** Compute live status from expiry date (overrides stale server status) */
function computeLiveStatus(cert: Pick<WorkerCert, "status" | "expiryDate">): WorkerCert["status"] {
  if (cert.status === "revoked") return "revoked";
  if (!cert.expiryDate) return cert.status;
  const expiry = parseDateOnly(cert.expiryDate);
  const now = new Date();
  const thirtyDays = new Date();
  thirtyDays.setDate(now.getDate() + 30);
  if (expiry < now) return "expired";
  if (expiry < thirtyDays) return "expiring_soon";
  return "valid";
}

// ─── Types for pending cert review ──────────────────────────────────

interface PendingCertReview {
  id: string;
  userId: string;
  userName: string | null;
  customCertName: string | null;
  certTypeName: string | null;
  certNumber: string | null;
  issuedDate: string | null;
  expiryDate: string | null;
  fileId: string | null;
  fileId2: string | null;
  notes: string | null;
  createdAt: string;
}

// ─── Add a custom credential to the credential list ──────────────────

function PromoteCustomDialog({ cert, onClose }: { cert: WorkerCert; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(cert.customCertName ?? "");
  const [category, setCategory] = useState<"trade" | "safety" | "training">(
    (cert as { customCategory?: "trade" | "safety" | "training" | null }).customCategory ?? "safety"
  );
  const [validity, setValidity] = useState("");
  const [description, setDescription] = useState("");

  const promote = useMutation({
    mutationFn: () =>
      apiPost(`/api/admin/certifications/${cert.id}/promote`, {
        name: name.trim(),
        category,
        description: description.trim() || undefined,
        defaultValidityMonths: Number(validity) > 0 ? Math.round(Number(validity)) : undefined,
      }),
    onSuccess: () => {
      toast.success("Added to the credential list");
      queryClient.invalidateQueries({ queryKey: ["admin", "worker-certs"] });
      queryClient.invalidateQueries({ queryKey: ["sub-credentials"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "cert-types"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "cert-compliance"] });
      queryClient.invalidateQueries({ queryKey: ["certifications", "types"] });
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Add to the credential list</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 py-4">
          <p className="text-xs text-muted-foreground">
            This makes "{cert.customCertName}" a standard credential everyone can pick. Records with the same name are linked to it, and you can fill in its details now.
          </p>
          <div className="space-y-1.5">
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Category</Label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as "trade" | "safety" | "training")}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="trade">Trade</option>
              <option value="safety">Safety</option>
              <option value="training">Training</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Valid for (months, optional)</Label>
            <Input type="number" min={1} value={validity} onChange={(e) => setValidity(e.target.value)} placeholder="e.g. 36" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Description (optional)</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <Button className="w-full" disabled={!name.trim() || promote.isPending} onClick={() => promote.mutate()}>
            {promote.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add to the credential list
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Pending Cert Review Banner ──────────────────────────────────────

function PendingCertReviewSection({ certTypes, subs = false }: { certTypes: CertType[]; subs?: boolean }) {
  const queryClient = useQueryClient();
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [selectedCertTypeId, setSelectedCertTypeId] = useState("");
  const [selectedCustomCategory, setSelectedCustomCategory] = useState<"trade" | "safety" | "training" | "">("");
  // Approve as a new standard credential everyone can pick (name/validity/description editable)
  const [reviewName, setReviewName] = useState("");
  const [addToList, setAddToList] = useState(false);
  const [listValidity, setListValidity] = useState("");
  const [listDescription, setListDescription] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: subs ? ["admin", "cert-review", "subs"] : ["admin", "cert-review"],
    queryFn: () => apiGet<{ data: PendingCertReview[] }>(`/api/admin/certifications/review${subs ? "?subs=1" : ""}`),
    refetchInterval: 60_000,
  });
  const pending = data?.data ?? [];

  const reviewMutation = useMutation({
    mutationFn: (vars: {
      id: string;
      reviewStatus: "approved" | "rejected";
      certTypeId?: string;
      customCategory?: string;
      customCertName?: string;
      createType?: { name: string; category: string; description?: string; defaultValidityMonths?: number };
    }) => {
      const { id, ...body } = vars;
      return apiPatch(`/api/admin/certifications/review/${id}`, body);
    },
    onSuccess: (_, vars) => {
      toast.success(
        vars.reviewStatus === "rejected"
          ? "Credential rejected"
          : vars.createType
            ? "Credential approved and added to the credential list"
            : "Credential approved"
      );
      setReviewingId(null);
      setSelectedCertTypeId("");
      setSelectedCustomCategory("");
      setAddToList(false);
      setListValidity("");
      setListDescription("");
      queryClient.invalidateQueries({ queryKey: ["admin", "cert-review"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "worker-certs"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "cert-types"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "cert-compliance"] });
      queryClient.invalidateQueries({ queryKey: ["certifications", "types"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading || pending.length === 0) return null;

  return (
    <div className="rounded-xl border border-red-400/40 bg-red-500/10 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0" />
        <p className="text-sm font-semibold text-red-700 dark:text-red-400">
          {pending.length} pending credential request{pending.length !== 1 ? "s" : ""}
        </p>
      </div>
      <div className="space-y-3">
        {pending.map((req) => (
          <div key={req.id} className="rounded-lg bg-surface-raised border border-border p-3 space-y-2">
            <div className="flex items-start gap-2">
              <Award className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text-primary">{req.userName ?? "Unknown"}</p>
                <p className="text-xs text-muted-foreground">
                  submitted: <strong>"{req.customCertName || req.certTypeName}"</strong>
                  {req.certNumber && <span className="ml-2">#{req.certNumber}</span>}
                  {req.issuedDate && <span className="ml-2">issued {formatDateOnly(req.issuedDate)}</span>}
                </p>
                {req.notes && <p className="text-xs text-muted-foreground italic mt-0.5">"{req.notes}"</p>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {req.fileId && (
                  <DocumentLink fileId={req.fileId} />
                )}
                {req.fileId2 && (
                  <DocumentLink fileId={req.fileId2} />
                )}
                <span className="text-[10px] text-muted-foreground">
                  {new Date(req.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
            {reviewingId === req.id ? (
              <div className="space-y-2 pt-1">
                {!selectedCertTypeId && (
                  <div>
                    <label className="text-xs text-muted-foreground">Credential name (you can correct it here)</label>
                    <Input
                      value={reviewName}
                      onChange={(e) => setReviewName(e.target.value)}
                      className="mt-1 h-8 text-xs"
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs text-muted-foreground">Map to credential type (optional)</label>
                  <SearchableSelect
                    size="sm"
                    className="mt-1"
                    value={selectedCertTypeId}
                    onChange={(v) => { setSelectedCertTypeId(v); setSelectedCustomCategory(""); }}
                    allLabel="— Keep as custom —"
                    allValue=""
                    options={certTypes.filter((t) => t.isActive).map((t) => ({ value: t.id, label: t.name, hint: CATEGORY_LABELS[t.category] }))}
                  />
                </div>
                {!selectedCertTypeId && (
                  <div>
                    <label className="text-xs text-muted-foreground">Category (required to approve)</label>
                    <select
                      value={selectedCustomCategory}
                      onChange={e => setSelectedCustomCategory(e.target.value as "trade" | "safety" | "training" | "")}
                      className="mt-1 flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                    >
                      <option value="">— Select a category —</option>
                      <option value="trade">Trade</option>
                      <option value="safety">Safety</option>
                      <option value="training">Training</option>
                    </select>
                  </div>
                )}
                {!selectedCertTypeId && (
                  <div className="rounded-md border border-border p-2 space-y-2">
                    <label className="flex items-start gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={addToList}
                        onChange={(e) => setAddToList(e.target.checked)}
                        className="mt-0.5"
                      />
                      <span>
                        <strong>Add to the credential list</strong>
                        <span className="block text-muted-foreground">Everyone can then pick it like a standard credential, and other workers' records with this name are linked to it.</span>
                      </span>
                    </label>
                    {addToList && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-muted-foreground">Valid for (months, optional)</label>
                          <Input
                            type="number"
                            min={1}
                            value={listValidity}
                            onChange={(e) => setListValidity(e.target.value)}
                            className="mt-1 h-8 text-xs"
                            placeholder="e.g. 36"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs text-muted-foreground">Description (optional)</label>
                          <Input
                            value={listDescription}
                            onChange={(e) => setListDescription(e.target.value)}
                            className="mt-1 h-8 text-xs"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1 h-8 text-xs"
                    disabled={reviewMutation.isPending || (!selectedCertTypeId && !selectedCustomCategory) || (!selectedCertTypeId && !reviewName.trim())}
                    onClick={() => {
                      const name = reviewName.trim();
                      reviewMutation.mutate({
                        id: req.id,
                        reviewStatus: "approved",
                        certTypeId: selectedCertTypeId || undefined,
                        customCategory: !selectedCertTypeId ? selectedCustomCategory || undefined : undefined,
                        customCertName: !selectedCertTypeId && name && name !== (req.customCertName ?? "") ? name : undefined,
                        createType: !selectedCertTypeId && addToList && selectedCustomCategory
                          ? {
                              name,
                              category: selectedCustomCategory,
                              description: listDescription.trim() || undefined,
                              defaultValidityMonths: Number(listValidity) > 0 ? Math.round(Number(listValidity)) : undefined,
                            }
                          : undefined,
                      });
                    }}
                  >
                    {reviewMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                    Approve
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    className="h-8 text-xs text-red-600 border-red-300 hover:bg-red-50"
                    disabled={reviewMutation.isPending}
                    onClick={() => reviewMutation.mutate({ id: req.id, reviewStatus: "rejected" })}
                  >
                    <XCircle className="h-3 w-3" /> Reject
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setReviewingId(null); setSelectedCertTypeId(""); setSelectedCustomCategory(""); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setReviewingId(req.id);
                  setSelectedCertTypeId("");
                  setReviewName(req.customCertName ?? "");
                  setAddToList(false);
                  setListValidity("");
                  setListDescription("");
                }}
                className="text-xs text-paris-red-text hover:underline font-medium"
              >
                Review →
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Export ─────────────────────────────────────────────────────────────

interface ExportCert {
  id: string;
  userId: string;
  userName: string | null;
  divisionName: string | null;
  certName: string | null;
  certTypeCategory: string | null;
  certNumber: string | null;
  issuedDate: string | null;
  expiryDate: string | null;
  status: string;
  fileId: string | null;
  mimeType: string | null;
}

function extFromMimeType(mimeType: string): string {
  const map: Record<string, string> = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/webp": "webp",
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  };
  return map[mimeType] || "bin";
}

function sanitizePath(s: string): string {
  return s.replace(/[/\\:*?"<>|]/g, "-").trim() || "Unknown";
}

function ExportPanel({ onClose }: { onClose: () => void }) {
  const [scope, setScope] = useState<"all" | "division" | "workers">("all");
  const [selectedDivisionId, setSelectedDivisionId] = useState("");
  const [selectedWorkerIds, setSelectedWorkerIds] = useState<string[]>([]);
  const [workerSearch, setWorkerSearch] = useState("");
  const [includeExpired, setIncludeExpired] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingZip, setIsExportingZip] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);

  const { data: divisionsData } = useQuery({
    queryKey: ["admin", "divisions"],
    queryFn: () => apiGet<{ data: Division[] }>("/api/admin/divisions"),
  });
  const { data: usersData } = useQuery({
    queryKey: ["admin", "org-users"],
    queryFn: () => apiGet<{ data: OrgUser[] }>("/api/users"),
  });
  const divisions = divisionsData?.data ?? [];
  const allUsers = usersData?.data ?? [];

  const filteredUsers = allUsers.filter(u => {
    const matchesDivision = !selectedDivisionId || u.divisionId === selectedDivisionId;
    const matchesSearch = !workerSearch || (u.name || u.email).toLowerCase().includes(workerSearch.toLowerCase());
    return matchesDivision && matchesSearch;
  });

  async function fetchExportData(): Promise<ExportCert[]> {
    const params = new URLSearchParams();
    if (scope === "division" && selectedDivisionId) params.set("divisionId", selectedDivisionId);
    if (scope === "workers" && selectedWorkerIds.length > 0) params.set("userIds", selectedWorkerIds.join(","));
    if (includeExpired) params.set("includeExpired", "true");
    if (includeInactive) params.set("includeInactive", "true");
    const res = await apiGet<{ data: ExportCert[] }>(`/api/admin/certifications/export?${params}`);
    return res.data;
  }

  async function handleExcelExport() {
    setIsExportingExcel(true);
    try {
      const certs = await fetchExportData();
      if (certs.length === 0) { toast.info("No valid credentials found for the selected scope"); return; }

      const exportDate = new Date().toLocaleDateString("en-CA");
      const headers = ["Worker", "Division", "Credential Name", "Cert #", "Category", "Issued Date", "Expiry Date", "Status"];
      const numCols = headers.length;

      const dataRows = certs.map(c => [
        c.userName || "",
        c.divisionName || "",
        c.certName || "",
        c.certNumber || "",
        c.certTypeCategory === "trade" ? "Trade Ticket"
          : c.certTypeCategory === "safety" ? "Safety"
          : c.certTypeCategory === "training" ? "Training"
          : "Custom",
        c.issuedDate || "",
        c.expiryDate || "",
        c.status === "expiring_soon" ? "Expiring Soon"
          : c.status === "expired" ? "Expired"
          : "Valid",
      ]);

      // Row 1 = title (merged), Row 2 = headers + filter, Rows 3+ = data
      const titleCell = `Paris Mechanical — Credentials Report | Exported: ${exportDate}`;
      const ws = XLSX.utils.aoa_to_sheet([
        [titleCell, ...Array(numCols - 1).fill("")],
        headers,
        ...dataRows,
      ]);

      // Merge title row across all columns
      ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } }];

      // Auto-filter on header row (row index 1)
      const lastCol = XLSX.utils.encode_col(numCols - 1);
      ws["!autofilter"] = { ref: `A2:${lastCol}2` };

      // Freeze title + header rows so data scrolls beneath them
      ws["!views"] = [{ state: "frozen", xSplit: 0, ySplit: 2, topLeftCell: "A3" }];

      // Column widths
      ws["!cols"] = headers.map((h, ci) => ({
        wch: Math.max(h.length, ...dataRows.map(r => String(r[ci] || "").length)) + 3,
      }));

      // ── Styles ──────────────────────────────────────────────────────────
      // xlsx-js-style requires 8-char ARGB (FF prefix) for border colors
      const thin = (argb: string) => ({ style: "thin" as const, color: { rgb: argb } });
      const dataBorder = { top: thin("FF999999"), bottom: thin("FF999999"), left: thin("FF999999"), right: thin("FF999999") };
      const hdrBorder  = { top: thin("FF7F1D1D"), bottom: thin("FF7F1D1D"), left: thin("FF7F1D1D"), right: thin("FF7F1D1D") };

      const titleStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 13, name: "Arial" },
        fill: { fgColor: { rgb: "991B1B" }, patternType: "solid" as const },
        alignment: { horizontal: "center" as const, vertical: "center" as const },
        border: hdrBorder,
      };
      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11, name: "Arial" },
        fill: { fgColor: { rgb: "B91C1C" }, patternType: "solid" as const },
        alignment: { horizontal: "center" as const, vertical: "center" as const },
        border: hdrBorder,
      };
      const rowStyle = (rowIdx: number) => ({
        font: { sz: 11, name: "Arial" },
        fill: { fgColor: { rgb: rowIdx % 2 === 0 ? "FEF2F2" : "FFFFFF" }, patternType: "solid" as const },
        alignment: { vertical: "center" as const },
        border: dataBorder,
      });
      const statusStyle = (val: string) => {
        const expiring = val === "Expiring Soon";
        const expired = val === "Expired";
        const color = expiring ? "92400E" : expired ? "7F1D1D" : "166534";
        const bg = expiring ? "FEF3C7" : expired ? "FEE2E2" : "DCFCE7";
        return {
          font: { bold: true, sz: 11, name: "Arial", color: { rgb: color } },
          fill: { fgColor: { rgb: bg }, patternType: "solid" as const },
          alignment: { horizontal: "center" as const, vertical: "center" as const },
          border: dataBorder,
        };
      };

      const totalSheetRows = 2 + dataRows.length; // title + header + data
      for (let R = 0; R < totalSheetRows; R++) {
        for (let C = 0; C < numCols; C++) {
          const addr = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[addr]) ws[addr] = { t: "s", v: "" };
          if (R === 0) {
            ws[addr].s = titleStyle;
          } else if (R === 1) {
            ws[addr].s = headerStyle;
          } else {
            const dataRowIdx = R - 2;
            ws[addr].s = C === numCols - 1
              ? statusStyle(String(ws[addr].v ?? ""))
              : rowStyle(dataRowIdx);
          }
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Credentials");
      XLSX.writeFile(wb, "Paris Mechanical - Credentials.xlsx", { cellStyles: true });
      toast.success(`Excel exported — ${certs.length} credential records`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    } finally {
      setIsExportingExcel(false);
    }
  }

  async function handleZipExport() {
    setIsExportingZip(true);
    setZipProgress(0);
    try {
      const certs = await fetchExportData();
      const withFiles = certs.filter(c => c.fileId);
      if (withFiles.length === 0) { toast.info("No credentials with documents in the selected scope"); return; }

      const zip = new JSZip();
      const root = zip.folder("Paris Mechanical")!;

      let skipped = 0;
      for (let i = 0; i < withFiles.length; i++) {
        const cert = withFiles[i];
        const division = sanitizePath(cert.divisionName || "No Division");
        const worker = sanitizePath(cert.userName || "Unknown Worker");
        const certName = sanitizePath(cert.certName || "Unknown");
        const expiry = cert.expiryDate || "no-expiry";
        const ext = extFromMimeType(cert.mimeType || "");
        const fileName = `${sanitizePath(cert.userName || "Unknown")},${certName},${expiry}.${ext}`;

        try {
          // Use /download to proxy through the worker — avoids R2 CORS issues
          const fileRes = await fetch(apiUrl(`/api/files/${cert.fileId}/download`), { credentials: "include" });
          if (!fileRes.ok) throw new Error(`HTTP ${fileRes.status}`);
          // A web page here means the request never reached the API: skip it rather than save the page as the document
          if ((fileRes.headers.get("content-type") ?? "").includes("text/html")) throw new Error("Got a web page instead of the document");
          const bytes = await fileRes.arrayBuffer();
          root.folder(division)!.folder(worker)!.file(fileName, bytes);
        } catch (err) {
          console.warn(`ZIP: skipped file for cert ${cert.id}:`, err);
          skipped++;
        }

        setZipProgress(Math.round(((i + 1) / withFiles.length) * 90));
      }

      setZipProgress(95);
      const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
      setZipProgress(100);

      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "Paris Mechanical - Credentials.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
      const downloaded = withFiles.length - skipped;
      if (downloaded === 0) {
        toast.warning("No documents could be downloaded — check browser console for details");
      } else {
        toast.success(`ZIP downloaded — ${downloaded} document${downloaded !== 1 ? "s" : ""}${skipped > 0 ? ` (${skipped} skipped)` : ""}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "ZIP export failed");
    } finally {
      setIsExportingZip(false);
      setZipProgress(0);
    }
  }

  const canExportWorkers = scope !== "workers" || selectedWorkerIds.length > 0;
  const isBusy = isExportingExcel || isExportingZip;

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Download className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-semibold">Export Credentials</p>
        </div>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Scope selector */}
      <div className="flex gap-2 flex-wrap">
        {(["all", "division", "workers"] as const).map(s => (
          <button
            key={s}
            type="button"
            onClick={() => { setScope(s); setSelectedDivisionId(""); setSelectedWorkerIds([]); setWorkerSearch(""); }}
            className={cn(
              "px-2.5 py-1 rounded-full text-xs font-medium transition-colors",
              scope === s ? "bg-paris-red text-white" : "bg-muted text-text-primary border border-border hover:bg-muted/70"
            )}
          >
            {s === "all" ? "All Org" : s === "division" ? "By Division" : "Select Workers"}
          </button>
        ))}
      </div>

      {/* Division dropdown */}
      {(scope === "division" || scope === "workers") && (
        <SearchableSelect
          size="sm"
          value={selectedDivisionId}
          onChange={(v) => { setSelectedDivisionId(v); setSelectedWorkerIds([]); }}
          allLabel="All Divisions"
          allValue=""
          options={divisions.map((d) => ({ value: d.id, label: d.name }))}
        />
      )}

      {/* Worker multi-select */}
      {scope === "workers" && (
        <div className="space-y-1.5">
          <Input
            type="search"
            placeholder="Search workers..."
            value={workerSearch}
            onChange={e => setWorkerSearch(e.target.value)}
            className="h-7 text-xs"
          />
          <div className="max-h-36 overflow-y-auto border border-input rounded-md divide-y divide-border">
            {filteredUsers.length === 0 ? (
              <p className="text-xs text-muted-foreground p-2 text-center">No workers found</p>
            ) : filteredUsers.map(u => (
              <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted/50">
                <input
                  type="checkbox"
                  checked={selectedWorkerIds.includes(u.id)}
                  onChange={e => {
                    if (e.target.checked) setSelectedWorkerIds(prev => [...prev, u.id]);
                    else setSelectedWorkerIds(prev => prev.filter(id => id !== u.id));
                  }}
                  className="h-3 w-3 accent-paris-red"
                />
                <span className="text-xs truncate">{u.name || u.email}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {selectedWorkerIds.length > 0 ? `${selectedWorkerIds.length} selected` : "Select at least one worker"}
          </p>
        </div>
      )}

      {/* Filter options */}
      <div className="border border-input rounded-md p-2.5 space-y-2 bg-muted/30">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Include in export</p>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeExpired}
            onChange={e => setIncludeExpired(e.target.checked)}
            className="h-3.5 w-3.5 accent-paris-red"
          />
          <span className="text-xs">Expired credentials</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={e => setIncludeInactive(e.target.checked)}
            className="h-3.5 w-3.5 accent-paris-red"
          />
          <span className="text-xs">Deactivated / removed workers</span>
        </label>
      </div>

      {/* ZIP progress bar */}
      {isExportingZip && zipProgress > 0 && (
        <div className="space-y-1">
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-paris-red transition-all duration-200"
              style={{ width: `${zipProgress}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground text-right">{zipProgress}%</p>
        </div>
      )}

      {/* Export buttons */}
      <div className="flex gap-2 flex-wrap">
        <Button
          size="sm" variant="outline"
          onClick={handleExcelExport}
          disabled={isBusy || !canExportWorkers}
          className="gap-1.5 text-xs"
        >
          {isExportingExcel ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
          Export Excel
        </Button>
        <Button
          size="sm" variant="outline"
          onClick={handleZipExport}
          disabled={isBusy || !canExportWorkers}
          className="gap-1.5 text-xs"
        >
          {isExportingZip ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          {isExportingZip ? `Downloading ${zipProgress}%` : "Download ZIP"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        By default exports active workers with valid &amp; expiring-soon credentials. Use the checkboxes above to include expired certs or deactivated workers. ZIP includes attached documents in a <code className="text-xs bg-muted px-1 rounded">Paris Mechanical / Division / Worker</code> folder structure.
      </p>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────

function AdminCertificationsPage() {
  const [activeTab, setActiveTab] = useState<"types" | "compliance" | "certs" | "subs">("certs");

  const tabs: { key: typeof activeTab; label: string }[] = [
    { key: "certs", label: "Paris Crew Credentials" },
    { key: "subs", label: "Subcontractors" },
    { key: "types", label: "Credential Types" },
    { key: "compliance", label: "Compliance" },
  ];

  const { data: certTypesData } = useQuery({
    queryKey: ["admin", "cert-types"],
    queryFn: () => apiGet<{ data: CertType[] }>("/api/admin/certifications/types"),
  });
  const certTypes = certTypesData?.data ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-text-primary">Credentials</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Manage credential types, worker credentials, and compliance.</p>
      </div>

      {/* Pending custom credential requests — always visible above tabs */}
      <PendingCertReviewSection certTypes={certTypes} />

      {/* Paris Crew Credentials with Subcontractors right under it; the other tabs beside them */}
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        {[tabs.filter((t) => t.key === "certs" || t.key === "subs"), tabs.filter((t) => t.key !== "certs" && t.key !== "subs")].map((group, gi) => (
          <div key={gi} className={cn("flex gap-2", gi === 0 ? "flex-col items-start" : "flex-wrap")}>
            {group.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-xs font-medium transition-colors",
                  activeTab === tab.key
                    ? "bg-paris-red text-white"
                    : "bg-card text-text-primary border border-border hover:bg-muted/50"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ))}
      </div>

      {activeTab === "certs" && <WorkerCertsPanel />}
      {activeTab === "types" && <CertTypesPanel />}
      {activeTab === "compliance" && <CompliancePanel />}
      {activeTab === "subs" && (
        <div className="space-y-4">
          <PendingCertReviewSection certTypes={certTypes} subs />
          <SubCredentialsList />
        </div>
      )}

      {/* Run Cert Expiry Check */}
      <CertExpiryCheckCard />

      <CertAssignmentApprovalPanel />
    </div>
  );
}

// ─── Floating approve/reject panel (deep-linked from Assignments) ─────────

interface CertAssignmentDetailForPanel {
  id: string;
  certType: { id: string; name: string } | null;
  completions: {
    userId: string;
    workerName: string;
    status: "assigned" | "submitted" | "approved";
    claimNote: string | null;
    userCertificationId: string | null;
  }[];
}

/**
 * Sticky bottom panel shown only when this page was opened via "View Crew's
 * Certs" from a certification assignment -- lets the admin approve/reject
 * right here instead of bouncing back and forth to the Assignments page.
 */
function CertAssignmentApprovalPanel() {
  const { assignmentId, workerId } = useSearch({ from: "/_authenticated/admin/certifications" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectNote, setRejectNote] = useState("");

  const { data } = useQuery({
    queryKey: ["admin", "cert-assignment-detail", assignmentId],
    queryFn: () => apiGet<{ data: CertAssignmentDetailForPanel }>(`/api/admin/cert-assignments/${assignmentId}`),
    enabled: !!assignmentId && !!workerId,
  });
  const completion = data?.data.completions.find((c) => c.userId === workerId);

  const approveMut = useMutation({
    mutationFn: () => apiPatch(`/api/admin/cert-assignments/${assignmentId}/completions/${workerId}/approve`, {}),
    onSuccess: () => {
      toast.success("Certification approved");
      queryClient.invalidateQueries({ queryKey: ["admin", "cert-assignments"] });
      navigate({ to: "/admin/assignments", search: { tab: "certifications", assignmentId, open: undefined } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rejectMut = useMutation({
    mutationFn: (adminNote: string) => apiPatch(`/api/admin/cert-assignments/${assignmentId}/completions/${workerId}/reject`, { adminNote }),
    onSuccess: () => {
      toast.success("Sent back to the worker");
      queryClient.invalidateQueries({ queryKey: ["admin", "cert-assignments"] });
      navigate({ to: "/admin/assignments", search: { tab: "certifications", assignmentId, open: undefined } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!assignmentId || !workerId || dismissed || !completion || completion.status !== "submitted") return null;

  return (
    <div className="fixed bottom-16 md:bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-lg">
      <div className="rounded-xl border border-border bg-surface-raised shadow-lg p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <Link
            to="/admin/assignments"
            search={{ tab: "certifications", assignmentId, open: undefined }}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-text-primary transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Assignment
          </Link>
          <button type="button" onClick={() => setDismissed(true)} className="text-muted-foreground hover:text-text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
          <p className="text-sm font-medium">
            {completion.workerName} — {data?.data.certType?.name ?? "Certification"}
          </p>
        </div>

        {completion.claimNote && (
          <p className="text-xs text-muted-foreground italic flex items-start gap-1.5">
            <StickyNote className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            "I already have this — {completion.claimNote}"
          </p>
        )}

        {!rejecting ? (
          <div className="flex gap-2">
            <Button size="sm" className="flex-1" onClick={() => approveMut.mutate()} disabled={approveMut.isPending}>
              {approveMut.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1.5" />}
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 text-destructive border-destructive/40 hover:bg-destructive/10"
              onClick={() => setRejecting(true)}
              disabled={rejectMut.isPending}
            >
              Reject
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="e.g. The file is illegible, please re-upload a clearer photo."
              rows={2}
              autoFocus
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                className="flex-1"
                disabled={!rejectNote.trim() || rejectMut.isPending}
                onClick={() => rejectMut.mutate(rejectNote.trim())}
              >
                {rejectMut.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                Send Back
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setRejecting(false); setRejectNote(""); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Cert Expiry Check Card ─────────────────────────────────────────

function CertExpiryCheckCard() {
  const [resetHistory, setResetHistory] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      apiPost<{ success: boolean; reset: boolean; message: string }>(
        `/api/admin/run-cert-expiry${resetHistory ? "?reset=true" : ""}`
      ),
    onSuccess: (data) => {
      toast.success(data.message || "Cert expiry check completed");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to run cert expiry check");
    },
  });

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-sm font-medium">Cert Expiry Check</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Manually trigger the expiry notification cron. Sends email/SMS for certs approaching expiry.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="shrink-0 gap-1.5"
          >
            {mutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            {mutation.isPending ? "Running..." : "Run Now"}
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="cert-expiry-reset"
            checked={resetHistory}
            onChange={(e) => setResetHistory(e.target.checked)}
            disabled={mutation.isPending}
          />
          <Label htmlFor="cert-expiry-reset" className="text-xs text-muted-foreground">
            Reset notification history (re-notifies all eligible certs)
          </Label>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Worker Certs Panel ────────────────��─────────────────────────────

function WorkerCertsPanel() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewingCert, setViewingCert] = useState<WorkerCert | null>(null);
  const [promotingCert, setPromotingCert] = useState<WorkerCert | null>(null);
  const [deleteError, setDeleteError] = useState<{ id: string; message: string } | null>(null);

  // Filters
  const [filterDivision, setFilterDivision] = useState("");
  const [filterCertType, setFilterCertType] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showArchive, setShowArchive] = useState(false);
  const { q: deepLinkQuery, assign: assignParam } = useSearch({ from: "/_authenticated/admin/certifications" });
  const userFilter = useUserFilter();
  const navigate = useNavigate();
  // Fast assignment for the person this list is filtered to (also opened by the
  // "Assign certification" quick action on their Records).
  const [assignOpen, setAssignOpen] = useState(assignParam === "1");
  function closeAssign() {
    setAssignOpen(false);
    if (assignParam) {
      navigate({
        to: ".",
        search: ((prev: Record<string, unknown>) => ({ ...prev, assign: undefined })) as never,
        replace: true,
      });
    }
  }
  // Only super admins and division admins can jump to a worker's profile.
  const { data: profileSession } = authClient.useSession();
  const canOpenProfile = ["admin", "div_admin"].includes(profileSession?.user?.role ?? "");
  const [search, setSearch] = useState(deepLinkQuery ?? "");
  const [page, setPage] = useState(1);
  const limit = 20;

  // Build query string
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("limit", String(limit));
  if (filterDivision) params.set("divisionId", filterDivision);
  if (filterCertType) params.set("certTypeId", filterCertType);
  if (filterStatus !== "all") params.set("status", filterStatus);
  if (showArchive) params.set("archived", "1");
  if (search.trim()) params.set("search", search.trim());
  if (userFilter.userId) params.set("userId", userFilter.userId);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "worker-certs", filterDivision, filterCertType, filterStatus, showArchive, search, page, userFilter.userId],
    queryFn: () =>
      apiGet<{ data: WorkerCert[]; total: number }>(
        `/api/admin/certifications?${params.toString()}`
      ),
  });

  const { data: divisionsData } = useQuery({
    queryKey: ["admin", "divisions"],
    queryFn: () => apiGet<{ data: Division[] }>("/api/admin/divisions"),
  });

  const { data: certTypesData } = useQuery({
    queryKey: ["admin", "cert-types"],
    queryFn: () => apiGet<{ data: CertType[] }>("/api/admin/certifications/types"),
  });

  const certs = data?.data || [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const divisions = divisionsData?.data || [];
  const certTypes = (certTypesData?.data || []).filter((t) => t.isActive);

  const deleteMutation = useMutation({
    mutationFn: async ({ id, force }: { id: string; force?: boolean }) => {
      setDeleteError(null);
      const url = force
        ? `${BASE_URL}/api/admin/certifications/${id}?force=true`
        : `${BASE_URL}/api/admin/certifications/${id}`;
      const res = await fetch(url, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (res.status === 401) {
        window.location.href = "/login";
        throw new Error("Session expired");
      }
      if (res.status === 403) {
        const body = await res.json();
        setDeleteError({ id, message: body.message || "Cannot delete -- retention policy" });
        throw new Error(body.message);
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || "Delete failed");
      }
    },
    onSuccess: () => {
      toast.success("Certification deleted");
      queryClient.invalidateQueries({ queryKey: ["admin", "worker-certs"] });
    },
    onError: () => {
      // Error is already handled via deleteError state or toast
    },
  });

  const loadingSkeleton = (
    <div className="space-y-2 py-4">
      <div className="h-4 bg-muted rounded animate-pulse" />
      <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
      <div className="h-4 bg-muted rounded animate-pulse w-1/2" />
    </div>
  );

  return (
    <div className="space-y-3">
      {userFilter.userId && (
        <div className="space-y-2">
          <UserFilterChip name={userFilter.userName} onClear={userFilter.clear} />
          <Button size="sm" onClick={() => setAssignOpen(true)}>
            <Award className="h-4 w-4 mr-1.5" />
            Assign certification{userFilter.userName ? ` to ${userFilter.userName.split(" ")[0]}` : ""}
          </Button>
        </div>
      )}
      {assignOpen && userFilter.userId && (
        <CreateCertAssignmentSheet
          prefill={{ certTypeId: null, workerIds: [userFilter.userId] }}
          onClose={closeAssign}
          onCreated={() => {
            closeAssign();
            queryClient.invalidateQueries({ queryKey: ["admin", "cert-assignments"] });
            queryClient.invalidateQueries({ queryKey: ["admin", "user-records"] });
          }}
        />
      )}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Paris Crew Credentials
        </h2>
        <div className="flex gap-1.5">
          <Button
            size="sm" variant="outline"
            onClick={() => { setShowExport(v => !v); setShowCreate(false); }}
            className="gap-1"
          >
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setShowCreate(!showCreate); setEditingId(null); setShowExport(false); }}>
            <Plus className="h-4 w-4 mr-1" />
            Add Credential
          </Button>
        </div>
      </div>

      {showExport && <ExportPanel onClose={() => setShowExport(false)} />}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search by worker name, email, cert type, or cert #..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="h-9 pl-8 text-sm"
        />
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="w-48">
          <SearchableSelect
            size="sm"
            value={filterDivision}
            onChange={(v) => { setFilterDivision(v); setPage(1); }}
            allLabel="All Divisions"
            allValue=""
            options={divisions.map((d) => ({ value: d.id, label: d.name }))}
          />
        </div>
        <div className="w-56">
          <SearchableSelect
            size="sm"
            value={filterCertType}
            onChange={(v) => { setFilterCertType(v); setPage(1); }}
            allLabel="All Credential Types"
            allValue=""
            searchPlaceholder="Type a credential name..."
            options={certTypes.map((t) => ({ value: t.id, label: t.name, hint: CATEGORY_LABELS[t.category] }))}
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          className="h-8 rounded-md border border-input bg-background px-2 text-xs"
        >
          <option value="all">All Statuses</option>
          <option value="valid">Valid</option>
          <option value="expiring_soon">Expiring Soon</option>
          <option value="expired">Expired</option>
          <option value="revoked">Revoked</option>
        </select>
        <button
          type="button"
          onClick={() => { setShowArchive((v) => !v); setPage(1); }}
          aria-pressed={showArchive}
          title="Expired credentials that have been renewed are kept here, not in the active list"
          className={
            showArchive
              ? "h-8 rounded-md border border-foreground bg-foreground px-3 text-xs font-medium text-background"
              : "h-8 rounded-md border border-input bg-background px-3 text-xs text-muted-foreground hover:bg-muted"
          }
        >
          {showArchive ? "Showing archive" : "Archive"}
        </button>
      </div>

      {showCreate && (
        <WorkerCertForm
          certTypes={certTypes}
          onClose={() => setShowCreate(false)}
        />
      )}

      {isLoading ? loadingSkeleton : certs.length === 0 && !showCreate ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Award className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No certifications found.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {certs.map((cert) =>
            editingId === cert.id ? (
              <WorkerCertForm
                key={cert.id}
                existing={cert}
                certTypes={certTypes}
                onClose={() => setEditingId(null)}
              />
            ) : (
              <Card key={cert.id}>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {canOpenProfile ? (
                          <Link
                            to="/admin/users"
                            search={{ open: cert.userId }}
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm font-medium text-paris-red-text hover:underline underline-offset-2"
                            title="Open profile"
                          >
                            {cert.userName || "Unknown"}
                          </Link>
                        ) : (
                          <span className="text-sm font-medium">{cert.userName || "Unknown"}</span>
                        )}
                        {cert.superseded ? (
                          <Badge variant="secondary" className="text-xs" title="Expired, replaced by a current record">Archived</Badge>
                        ) : (
                          <StatusBadge status={computeLiveStatus(cert)} />
                        )}
                        {cert.divisionName && (
                          <Badge variant="secondary" className="text-xs px-1.5 py-0">
                            {cert.divisionName}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-xs text-muted-foreground">
                          {cert.certTypeName || cert.customCertName || "\u2014"}
                        </span>
                        {cert.certNumber && (
                          <span className="text-xs text-muted-foreground">
                            #{cert.certNumber}
                          </span>
                        )}
                        {cert.expiryDate && (
                          <span className="text-xs text-muted-foreground">
                            Exp: {formatDateOnly(cert.expiryDate, "en-CA")}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0 items-center">
                      {canOpenProfile && !cert.certTypeId && cert.reviewStatus === "approved" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          onClick={() => setPromotingCert(cert)}
                          title="Add this custom credential to the credential list"
                        >
                          Add to list
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-blue-600 hover:text-blue-700"
                        onClick={() => setViewingCert(cert)}
                        title="View details"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => { setEditingId(cert.id); setShowCreate(false); }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => {
                          deleteWithUndo(
                            `${cert.userName ?? "cert"} — ${cert.certTypeName ?? cert.customCertName ?? "record"}`,
                            () => deleteMutation.mutateAsync({ id: cert.id })
                          );
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  {/* Inline retain error with force-delete override */}
                  {deleteError?.id === cert.id && (
                    <div className="flex items-start gap-2 p-2 rounded-md bg-yellow-500/10 border border-yellow-500/30">
                      <ShieldAlert className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-yellow-700 dark:text-yellow-400">
                          {deleteError.message}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-7 text-xs"
                            onClick={() => {
                              if (
                                window.confirm(
                                  "This will permanently delete the record, bypassing retention policy. Use only for mistakenly entered certs. Continue?"
                                )
                              ) {
                                deleteMutation.mutate({ id: cert.id, force: true });
                              }
                            }}
                          >
                            Force Delete
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs"
                            onClick={() => setDeleteError(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-muted-foreground">
            {total} total &middot; Page {page} of {totalPages}
          </p>
          <div className="flex gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {promotingCert && <PromoteCustomDialog cert={promotingCert} onClose={() => setPromotingCert(null)} />}

      <CertViewSheet
        cert={viewingCert}
        onClose={() => setViewingCert(null)}
      />
    </div>
  );
}

// ─── Cert View Sheet (with photo preview) ────────────────────────────

function DocumentPreview({ fileId, label }: { fileId: string | null; label: string }) {
  const { data: fileUrlData, isLoading: loadingUrl } = useQuery({
    queryKey: ["cert-file-url", fileId],
    queryFn: async () => {
      if (!fileId) return null;
      return await apiGet<{ url: string }>(`/api/files/${fileId}/url`);
    },
    enabled: !!fileId,
  });
  const fileUrl = fileUrlData?.url ?? null;

  if (!fileId) return null;

  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
      <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
        {loadingUrl ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : fileUrl ? (
          <>
            {/* Image or PDF */}
            <img
              src={fileUrl}
              alt={label}
              className="w-full max-h-96 object-contain bg-black/5"
              onError={(e) => {
                // If not an image (e.g. PDF), show fallback link
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
            <button
              type="button"
              onClick={async () => {
                if (!fileUrl) return;
                if (fileUrl.includes("/api/files/")) {
                  const r = await fetch(fileUrl, { credentials: "include" });
                  if (!r.ok) { toast.error("Failed to load document"); return; }
                  const blob = await r.blob();
                  const blobUrl = URL.createObjectURL(blob);
                  window.open(blobUrl, "_blank");
                  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                } else {
                  window.open(fileUrl, "_blank");
                }
              }}
              className="flex w-full items-center justify-center gap-2 py-2 border-t border-border text-sm text-blue-600 hover:bg-muted/50"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open full document
            </button>
          </>
        ) : (
          <div className="flex items-center justify-center h-48 text-sm text-muted-foreground">
            Could not load document
          </div>
        )}
      </div>
    </div>
  );
}

function CertViewSheet({
  cert,
  onClose,
}: {
  cert: WorkerCert | null;
  onClose: () => void;
}) {
  if (!cert) return null;

  const liveStatus = computeLiveStatus(cert);
  const certName = cert.certTypeName || cert.customCertName || "Unknown";

  return (
    <Sheet open={!!cert} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Certification Details</SheetTitle>
          <SheetDescription>
            View full certificate information and document.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Document preview(s) */}
          <DocumentPreview fileId={cert.fileId} label={cert.fileId2 ? "Front" : "Document"} />
          <DocumentPreview fileId={cert.fileId2} label="Back" />

          {!cert.fileId && !cert.fileId2 && (
            <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
              No document attached
            </div>
          )}

          {/* Details */}
          <div className="space-y-3">
            <DetailRow label="Worker" value={cert.userName || "Unknown"} />
            <DetailRow label="Certification" value={certName} />
            {cert.customCertName && !cert.certTypeName && (
              <DetailRow label="Type" value="Custom entry" />
            )}
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                Status
              </span>
              <StatusBadge status={liveStatus} />
            </div>
            {cert.divisionName && (
              <DetailRow label="Division" value={cert.divisionName} />
            )}
            {cert.certNumber && (
              <DetailRow label="Certificate #" value={cert.certNumber} />
            )}
            {cert.issuedDate && (
              <DetailRow
                label="Issued"
                value={formatDateOnly(cert.issuedDate, "en-CA")}
              />
            )}
            <DetailRow
              label="Expires"
              value={cert.expiryDate ? formatDateOnly(cert.expiryDate, "en-CA") : "No expiry"}
            />
            {cert.notes && (
              <div className="space-y-1 pt-2 border-t border-border">
                <span className="text-xs text-muted-foreground uppercase tracking-wider">
                  Notes
                </span>
                <p className="text-sm text-text-primary whitespace-pre-wrap">
                  {cert.notes}
                </p>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-muted-foreground uppercase tracking-wider">
        {label}
      </span>
      <span className="text-sm text-text-primary text-right">{value}</span>
    </div>
  );
}

// ─── Status Badge ────────────���───────────────────────────────────────

function StatusBadge({ status }: { status: WorkerCert["status"] }) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  return (
    <Badge className={cn(config.className, "text-xs gap-0.5 px-1.5 py-0")}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

// ─── Document Link ───────────────────────────────────────────────────

function DocumentLink({ fileId }: { fileId: string | null }) {
  const [loading, setLoading] = useState(false);

  if (!fileId) return null;

  const handleView = async () => {
    setLoading(true);
    try {
      const res = await apiGet<{ url: string }>(`/api/files/${fileId}/url`);
      const url = res.url;

      // When R2 presigning is not configured (staging / dev), the URL points
      // back to the worker's /raw endpoint which requires auth cookies.
      // window.open across origins doesn't reliably forward cookies, so
      // fetch the bytes through the authenticated client and open a blob URL.
      if (url.includes("/api/files/")) {
        const rawRes = await fetch(url, { credentials: "include" });
        if (!rawRes.ok) throw new Error("Failed to load file");
        const blob = await rawRes.blob();
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      } else {
        window.open(url, "_blank");
      }
    } catch {
      toast.error("Failed to load document");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      size="icon"
      variant="ghost"
      className="h-8 w-8 text-blue-600 hover:text-blue-700"
      onClick={handleView}
      disabled={loading}
      title="View document"
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
    </Button>
  );
}

// ─── Worker Cert Form ────────────────────────────────────────────────

function WorkerCertForm({
  existing,
  certTypes,
  onClose,
}: {
  existing?: WorkerCert;
  certTypes: CertType[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload, isUploading, isCompressing, progress } = useFileUpload();

  // Second page (e.g. back of a card/license) -- optional, own upload slot.
  const fileInputRef2 = useRef<HTMLInputElement>(null);
  const { upload: upload2, isUploading: isUploading2, isCompressing: isCompressing2, progress: progress2 } = useFileUpload();
  const [fileId2, setFileId2] = useState(existing?.fileId2 || "");
  const [fileName2, setFileName2] = useState("");
  const [showBackPage, setShowBackPage] = useState(!!existing?.fileId2);

  // Fetch org users for worker dropdown
  const { data: usersData } = useQuery({
    queryKey: ["admin", "org-users"],
    queryFn: () => apiGet<{ data: OrgUser[] }>("/api/users"),
  });
  const users = usersData?.data || [];

  const [userId, setUserId] = useState(existing?.userId || "");
  const [workerQuery, setWorkerQuery] = useState("");
  const [certTypeId, setCertTypeId] = useState(existing?.certTypeId || "");
  const [certNumber, setCertNumber] = useState(existing?.certNumber || "");
  const [issuedDate, setIssuedDate] = useState(existing?.issuedDate?.split("T")[0] || "");
  const [expiryDate, setExpiryDate] = useState(existing?.expiryDate?.split("T")[0] || "");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [fileId, setFileId] = useState(existing?.fileId || "");
  const [fileName, setFileName] = useState("");

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await upload(file, {
        fileName: file.name,
        mimeType: file.type,
        purpose: "certification",
      });
      setFileId(result.fileId);
      setFileName(file.name);
      toast.success("Document uploaded");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      toast.error(message);
    }
    e.target.value = "";
  };

  const handleFileSelect2 = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await upload2(file, {
        fileName: file.name,
        mimeType: file.type,
        purpose: "certification",
      });
      setFileId2(result.fileId);
      setFileName2(file.name);
      toast.success("Back page uploaded");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      toast.error(message);
    }
    e.target.value = "";
  };

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      existing
        ? apiPatch(`/api/admin/certifications/${existing.id}`, body)
        : apiPost("/api/admin/certifications", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "worker-certs"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "cert-compliance"] });
      toast.success(existing ? "Updated" : "Certification assigned");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // fileId is required for new certs; for edits, existing fileId counts
  const hasFile = !!(fileId || existing?.fileId);
  const canSubmit = existing
    ? !!(issuedDate && hasFile)
    : !!(userId && certTypeId && issuedDate && hasFile);

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">
            {existing ? "Edit" : "Assign"} Credential
          </span>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Row 1: Worker */}
        <div>
          <Label className="text-xs">Worker <span className="text-paris-red-text">*</span></Label>
          <SearchableSelect
            value={userId}
            onChange={setUserId}
            disabled={!!existing}
            allLabel="Select worker..."
            allValue=""
            searchPlaceholder="Type a worker name..."
            options={users.map((u) => ({ value: u.id, label: u.name || u.email, hint: u.name ? u.email : null }))}
          />
        </div>

        {/* Row 2: Cert Type + Cert Number */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Credential Type <span className="text-paris-red-text">*</span></Label>
            <SearchableSelect
              value={certTypeId}
              onChange={setCertTypeId}
              disabled={!!existing}
              allLabel="Select type..."
              allValue=""
              searchPlaceholder="Type a credential name..."
              options={certTypes.map((t) => ({ value: t.id, label: t.name, hint: CATEGORY_LABELS[t.category] }))}
            />
          </div>
          <div>
            <Label className="text-xs">Cert Number</Label>
            <Input
              value={certNumber}
              onChange={(e) => setCertNumber(e.target.value)}
              className="h-9 text-sm"
              placeholder="e.g., FA-2024-001"
            />
          </div>
        </div>

        {/* Row 3: Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">
              Issued Date <span className="text-paris-red-text">*</span>
            </Label>
            <Input
              type="date"
              value={issuedDate}
              onChange={(e) => setIssuedDate(e.target.value)}
              className="h-9 text-sm"
              min="1900-01-01"
              max="2099-12-31"
              required
            />
          </div>
          <div>
            <Label className="text-xs">Expiry Date</Label>
            <Input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="h-9 text-sm"
              min="1900-01-01"
              max="2099-12-31"
            />
            {!expiryDate && certTypeId && (() => {
              const ct = certTypes.find((t) => t.id === certTypeId);
              return ct?.defaultValidityMonths ? (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Auto-calculated: {ct.defaultValidityMonths} months from issue date
                </p>
              ) : null;
            })()}
          </div>
        </div>

        {/* Row 4: Document upload (required) */}
        <div>
          <Label className="text-xs">
            Document (PDF, JPG, PNG) <span className="text-paris-red-text">*</span>
          </Label>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Printed on both sides? You can add the back of the card below.
          </p>
          <div className="flex items-center gap-2 mt-1">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="gap-1"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {isCompressing ? "Optimizing..." : `${progress}%`}
                </>
              ) : (
                <>
                  <Upload className="h-3.5 w-3.5" />
                  Upload
                </>
              )}
            </Button>
            {hasFile && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" />
                {fileName || "Document attached"}
              </span>
            )}
            {!hasFile && (
              <span className="text-xs text-muted-foreground">Required</span>
            )}
          </div>
        </div>

        {/* Optional back page — for licenses/cards printed on both sides */}
        {!showBackPage && !fileId2 && (
          <button
            type="button"
            onClick={() => setShowBackPage(true)}
            className="text-xs text-blue-600 hover:underline"
          >
            + Add back of document (optional)
          </button>
        )}
        {(showBackPage || fileId2) && (
          <div>
            <Label className="text-xs">Back of Document <span className="text-muted-foreground">(optional)</span></Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                ref={fileInputRef2}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={handleFileSelect2}
                className="hidden"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => fileInputRef2.current?.click()}
                disabled={isUploading2}
                className="gap-1"
              >
                {isUploading2 ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {isCompressing2 ? "Optimizing..." : `${progress2}%`}
                  </>
                ) : (
                  <>
                    <Upload className="h-3.5 w-3.5" />
                    Upload
                  </>
                )}
              </Button>
              {fileId2 && (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <FileText className="h-3.5 w-3.5" />
                  {fileName2 || "Back page attached"}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Row 5: Notes */}
        <div>
          <Label className="text-xs">Notes</Label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="Optional notes..."
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            disabled={!canSubmit || mutation.isPending || isUploading}
            onClick={() => {
              if (!issuedDate) {
                toast.error("Issue date is required");
                return;
              }
              if (!hasFile) {
                toast.error("Document upload is required");
                return;
              }
              const body: Record<string, unknown> = { issuedDate };
              if (!existing) {
                body.userId = userId;
                body.certTypeId = certTypeId;
              }
              if (certNumber.trim()) body.certNumber = certNumber.trim();
              else if (existing) body.certNumber = null;
              if (expiryDate) body.expiryDate = expiryDate;
              else if (existing) body.expiryDate = null;
              if (fileId) body.fileId = fileId;
              if (fileId2 !== (existing?.fileId2 || "")) body.fileId2 = fileId2 || null;
              if (notes.trim()) body.notes = notes.trim();
              else if (existing) body.notes = null;
              mutation.mutate(body);
            }}
          >
            {mutation.isPending ? "Saving..." : existing ? "Save" : "Assign"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Cert Types Panel (existing, unchanged) ──────────────────────────

function CertTypesPanel() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // The type whose new validity is being applied to the certificates that already exist
  const [revising, setRevising] = useState<CertType | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "cert-types"],
    queryFn: () =>
      apiGet<{ data: CertType[] }>("/api/admin/certifications/types"),
  });

  const [typeQuery, setTypeQuery] = useState("");
  // Alphabetical by name (ignoring upper/lower case), filtered by the search box.
  const allTypes = data?.data || [];
  const types = [...allTypes]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true }))
    .filter((t) => matchesQuery(typeQuery, t.name, t.category === "trade" ? "trade ticket" : t.category));

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiPatch(`/api/admin/certifications/types/${id}`, { isActive: false }),
    onSuccess: () => {
      toast.success("Cert type deactivated");
      queryClient.invalidateQueries({ queryKey: ["admin", "cert-types"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) =>
      apiPatch(`/api/admin/certifications/types/${id}`, { isActive: true }),
    onSuccess: () => {
      toast.success("Cert type activated");
      queryClient.invalidateQueries({ queryKey: ["admin", "cert-types"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Permanent delete (super admin only -- backend enforces this). Any
  // existing certifications under this type are kept and converted to a
  // custom entry rather than lost.
  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiDelete(`/api/admin/certifications/types/${id}?force=true`),
    onSuccess: () => {
      toast.success("Cert type permanently deleted");
      queryClient.invalidateQueries({ queryKey: ["admin", "cert-types"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading) {
    return (
      <div className="space-y-2 py-4">
        <div className="h-4 bg-muted rounded animate-pulse" />
        <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Credential Types
        </h2>
        <Button size="sm" variant="outline" onClick={() => setShowCreate(!showCreate)}>
          <Plus className="h-4 w-4 mr-1" />
          Add Credential Type
        </Button>
      </div>

      {showCreate && (
        <CertTypeForm onClose={() => setShowCreate(false)} />
      )}

      {allTypes.length > 0 && (
        <PickerSearch value={typeQuery} onChange={setTypeQuery} placeholder="Search credential types…" />
      )}

      {types.length === 0 && allTypes.length > 0 && (
        <p className="text-sm text-muted-foreground py-6 text-center">No credential types match "{typeQuery}".</p>
      )}

      {types.length === 0 && !showCreate && allTypes.length === 0 && (
        <p className="text-sm text-muted-foreground py-8 text-center">
          No certification types yet.
        </p>
      )}

      {types.map((ct) =>
        editingId === ct.id ? (
          <CertTypeForm
            key={ct.id}
            existing={ct}
            onClose={() => setEditingId(null)}
            onValidityChanged={(t) => setRevising(t)}
          />
        ) : (
          <Card key={ct.id}>
            <CardContent className="p-4 flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{ct.name}</span>
                  {ct.category && (
                    <Badge variant="secondary" className="text-xs px-1.5 py-0 capitalize">
                      {ct.category === "trade" ? "Trade Ticket" : ct.category === "safety" ? "Safety" : "Training"}
                    </Badge>
                  )}
                  {ct.isRequired && (
                    <Badge className="bg-blue-500/15 text-blue-700 dark:text-blue-400 text-xs px-1.5 py-0">
                      Required
                    </Badge>
                  )}
                  {!ct.isActive && (
                    <Badge variant="secondary" className="text-xs px-1.5 py-0">
                      Inactive
                    </Badge>
                  )}
                </div>
                {ct.description && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {ct.description}
                  </p>
                )}
                <div className="flex gap-3 mt-0.5">
                  {ct.defaultValidityMonths && (
                    <p className="text-xs text-muted-foreground">
                      Valid {ct.defaultValidityMonths} months
                    </p>
                  )}
                  {!ct.requiresExpiry && (
                    <p className="text-xs text-muted-foreground">No expiry</p>
                  )}
                  {!ct.requiresFile && (
                    <p className="text-xs text-muted-foreground">No doc required</p>
                  )}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                {ct.defaultValidityMonths && ct.requiresExpiry && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    title="Apply the validity to certificates that already exist"
                    aria-label="Apply the validity to existing certificates"
                    onClick={() => setRevising(ct)}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={() => setEditingId(ct.id)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                {ct.isActive ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => {
                      if (window.confirm("Deactivate this credential type? Workers will no longer be able to select it, but existing records are kept.")) {
                        deleteMutation.mutate(ct.id);
                      }
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                ) : (
                  <>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-emerald-600 hover:text-emerald-700"
                      onClick={() => activateMutation.mutate(ct.id)}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Permanently delete "${ct.name}"? Any existing certifications under this type will be kept, converted to a custom entry. This cannot be undone.`
                          )
                        ) {
                          hardDeleteMutation.mutate(ct.id);
                        }
                      }}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )
      )}
      <CertValidityDialog certType={revising} onClose={() => setRevising(null)} />
    </div>
  );
}

function CertTypeForm({
  existing,
  onClose,
  onValidityChanged,
}: {
  existing?: CertType;
  onClose: () => void;
  /** Called after saving when the validity period changed, so the new validity can be applied to existing certificates. */
  onValidityChanged?: (t: CertType) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(existing?.name || "");
  const [description, setDescription] = useState(existing?.description || "");
  const [validityMonths, setValidityMonths] = useState(
    existing?.defaultValidityMonths?.toString() || ""
  );
  const [isRequired, setIsRequired] = useState(existing?.isRequired ?? false);
  const [category, setCategory] = useState<"trade" | "safety" | "training">(
    existing?.category ?? "safety"
  );
  const [requiresExpiry, setRequiresExpiry] = useState(existing?.requiresExpiry ?? true);
  const [requiresFile, setRequiresFile] = useState(existing?.requiresFile ?? true);

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      existing
        ? apiPatch<{ data: CertType; validityChanged?: { from: number | null; to: number | null } | null }>(`/api/admin/certifications/types/${existing.id}`, body)
        : apiPost("/api/admin/certifications/types", body),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "cert-types"] });
      toast.success(existing ? "Updated" : "Created");
      onClose();
      // A policy change: offer to apply it to the certificates that already exist
      const changed = (res as { validityChanged?: { to: number | null } | null; data?: CertType })?.validityChanged;
      if (existing && changed?.to) onValidityChanged?.({ ...existing, ...(res as { data: CertType }).data });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">
            {existing ? "Edit" : "New"} Credential Type
          </span>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Category + Name row */}
        <div>
          <Label className="text-xs">Category *</Label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as "trade" | "safety" | "training")}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm mt-1"
          >
            <option value="trade">Trade Ticket</option>
            <option value="safety">Safety Certification</option>
            <option value="training">Training Record</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 text-sm" />
          </div>
          <div>
            <Label className="text-xs">Validity (months)</Label>
            <Input
              type="number"
              value={validityMonths}
              onChange={(e) => setValidityMonths(e.target.value)}
              className="h-9 text-sm"
              placeholder="e.g., 12"
              disabled={!requiresExpiry}
            />
          </div>
        </div>

        <div>
          <Label className="text-xs">Description</Label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        {/* Toggles */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
              id="cred-required"
            />
            <Label htmlFor="cred-required" className="text-xs">Required for all workers</Label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={requiresExpiry}
              onChange={(e) => {
                setRequiresExpiry(e.target.checked);
                if (!e.target.checked) setValidityMonths("");
              }}
              id="cred-requires-expiry"
            />
            <Label htmlFor="cred-requires-expiry" className="text-xs">Has expiry date</Label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={requiresFile}
              onChange={(e) => setRequiresFile(e.target.checked)}
              id="cred-requires-file"
            />
            <Label htmlFor="cred-requires-file" className="text-xs">Requires document upload</Label>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            disabled={!name.trim() || mutation.isPending}
            onClick={() =>
              mutation.mutate({
                name: name.trim(),
                category,
                description: description.trim() || undefined,
                // Editing: send null to clear a stored validity (omitting it would leave the old value).
                defaultValidityMonths: requiresExpiry && validityMonths
                  ? parseInt(validityMonths)
                  : existing ? null : undefined,
                isRequired,
                requiresExpiry,
                requiresFile,
              })
            }
          >
            {mutation.isPending ? "Saving..." : existing ? "Save" : "Create"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Compliance Panel (existing, unchanged) ──��───────────────────────

/** Everyone who holds one credential type, with status and expiry. Names open the profile for admins. */
function CertHolders({ certTypeId, customName, canOpenProfile }: { certTypeId: string | null; customName: string; canOpenProfile: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "cert-holders", certTypeId ?? `custom:${customName}`],
    queryFn: () =>
      apiGet<{ data: WorkerCert[]; total: number }>(
        certTypeId
          ? `/api/admin/certifications?certTypeId=${encodeURIComponent(certTypeId)}&reviewStatus=approved&limit=100`
          : `/api/admin/certifications?search=${encodeURIComponent(customName)}&reviewStatus=approved&limit=100`
      ),
    staleTime: 0,
  });
  // Custom credentials have no type to filter on: search by name, then keep only the
  // untyped records whose custom name matches exactly (ignoring case).
  const matching = (data?.data ?? []).filter(
    (r) => !r.superseded && (certTypeId || (!r.certTypeId && (r.customCertName ?? "").trim().toLowerCase() === customName.trim().toLowerCase()))
  );
  const rows = [...matching].sort((a, b) =>
    (a.userName ?? "").localeCompare(b.userName ?? "", undefined, { sensitivity: "base" })
  );

  if (isLoading) return <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />;
  if (rows.length === 0) return <p className="text-xs text-muted-foreground">Nobody has this credential yet.</p>;

  return (
    <div className="space-y-1">
      <div className="divide-y rounded-md border">
        {rows.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
            {canOpenProfile ? (
              <Link
                to="/admin/users"
                search={{ open: r.userId }}
                className="text-sm font-medium text-paris-red-text hover:underline underline-offset-2"
              >
                {r.userName || "Unknown"}
              </Link>
            ) : (
              <span className="text-sm font-medium">{r.userName || "Unknown"}</span>
            )}
            {r.divisionName && <span className="text-xs text-muted-foreground">{r.divisionName}</span>}
            <span className="ml-auto text-xs text-muted-foreground">
              {r.expiryDate ? `Expires ${formatDateOnly(r.expiryDate, "en-CA")}` : "No expiry"}
            </span>
            <StatusBadge status={computeLiveStatus(r)} />
          </div>
        ))}
      </div>
      {certTypeId && (data?.total ?? 0) > rows.length && (
        <p className="text-xs text-muted-foreground">Showing the first {rows.length} of {data?.total}.</p>
      )}
    </div>
  );
}

function CompliancePanel() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "cert-compliance"],
    queryFn: () =>
      apiGet<{ data: ComplianceEntry[]; subData: ComplianceEntry[]; totalUsers: number; crewUsers: number; subUsers: number }>(
        "/api/admin/certifications/compliance"
      ),
  });

  const crew = data?.data || [];
  const subs = data?.subData || [];

  if (isLoading) {
    return (
      <div className="space-y-2 py-4">
        <div className="h-4 bg-muted rounded animate-pulse" />
        <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
      </div>
    );
  }

  if (crew.length === 0 && subs.length === 0) {
    return (
      <div className="space-y-6">
        <MissingFinder />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Award className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">
              No certification data yet.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <MissingFinder />

      <p className="text-xs text-muted-foreground">
        {data?.totalUsers ?? 0} total users · {data?.crewUsers ?? 0} Paris crew users · {data?.subUsers ?? 0} subcontractor crew users
      </p>

      <div className="space-y-3">
        <p className="text-sm font-semibold">Paris crew compliance <span className="font-normal text-muted-foreground">({data?.crewUsers ?? 0} users)</span></p>
        {crew.length === 0 ? <p className="text-sm text-muted-foreground">No credential data for the Paris crew yet.</p> : <ComplianceCards entries={crew} />}
      </div>

      <div className="space-y-3 border-t pt-5">
        <p className="text-sm font-semibold">Subcontractor compliance <span className="font-normal text-muted-foreground">({data?.subUsers ?? 0} users)</span></p>
        {subs.length === 0 ? <p className="text-sm text-muted-foreground">No credential data for subcontractors yet.</p> : <ComplianceCards entries={subs} subs />}
      </div>
    </div>
  );
}

interface MissingPerson {
  id: string;
  name: string;
  isSub: boolean;
  age: number | null;
  division: string | null;
  jobTitle: string | null;
  missing: { certTypeId: string; name: string; reason: "none" | "expired" | "revoked" | "expiring_soon"; expiryDate: string | null }[];
}

const MISSING_LABEL = { none: "None on file", expired: "Expired", revoked: "Revoked", expiring_soon: "Expiring soon" } as const;
const MISSING_CLASS = {
  none: "bg-red-500/15 text-red-700 dark:text-red-400",
  expired: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  revoked: "bg-red-500/15 text-red-700 dark:text-red-400",
  expiring_soon: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
} as const;

/**
 * "Who is missing": pick one or more credentials and see who does NOT have them, for example the workers under 25
 * with no WHMIS for a young-worker orientation. Narrow it by age or birth dates, division and crew / subcontractors,
 * export the list, or assign the credential to everyone listed.
 */
function MissingFinder() {
  const [open, setOpen] = useState(false);
  const [typeIds, setTypeIds] = useState<string[]>([]);
  const [typeQuery, setTypeQuery] = useState("");
  const [who, setWho] = useState<"crew" | "subs" | "both">("crew");
  const [divisionId, setDivisionId] = useState("");
  const [ageMin, setAgeMin] = useState("");
  const [ageMax, setAgeMax] = useState("");
  const [bornFrom, setBornFrom] = useState("");
  const [bornTo, setBornTo] = useState("");
  const [countExpired, setCountExpired] = useState(true);
  const [countSoon, setCountSoon] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: typesData } = useQuery({
    queryKey: ["admin", "cert-types"],
    queryFn: () => apiGet<{ data: CertType[] }>("/api/admin/certifications/types"),
    enabled: open,
  });
  const { data: divisionsData } = useQuery({
    queryKey: ["admin", "divisions"],
    queryFn: () => apiGet<{ data: Division[] }>("/api/admin/divisions"),
    enabled: open,
  });
  const types = (typesData?.data ?? []).filter((t) => t.isActive).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  const divisions = divisionsData?.data ?? [];

  const params = new URLSearchParams({ certTypeIds: typeIds.join(","), who });
  if (divisionId && who !== "subs") params.set("divisionId", divisionId);
  if (ageMin) params.set("ageMin", ageMin);
  if (ageMax) params.set("ageMax", ageMax);
  if (bornFrom) params.set("bornFrom", bornFrom);
  if (bornTo) params.set("bornTo", bornTo);
  if (!countExpired) params.set("expired", "0");
  if (countSoon) params.set("soon", "1");

  const { data: result, isFetching } = useQuery({
    queryKey: ["admin", "cert-missing", params.toString()],
    queryFn: () => apiGet<{ data: MissingPerson[]; checked: number }>(`/api/admin/certifications/missing?${params}`),
    enabled: open && typeIds.length > 0,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
  const people = typeIds.length > 0 ? result?.data ?? [] : [];

  const agePresets = [
    { label: "Under 18", min: "", max: "17" },
    { label: "Under 25", min: "", max: "24" },
    { label: "25 to 39", min: "25", max: "39" },
    { label: "40 and over", min: "40", max: "" },
  ];
  const filtersOn = !!(divisionId || ageMin || ageMax || bornFrom || bornTo);
  const toggleType = (id: string) => setTypeIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length < 10 ? [...cur, id] : cur));
  const typeName = (id: string) => types.find((t) => t.id === id)?.name ?? "";

  function exportCsv() {
    const esc = (v: string | number | null) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [["Name", "Age", "Division", "Job title", "Type", "Missing", "Status"].map(esc).join(",")];
    for (const p of people) {
      lines.push([p.name, p.age, p.division, p.jobTitle, p.isSub ? "Subcontractor" : "Paris crew", p.missing.map((m) => m.name).join("; "), p.missing.map((m) => MISSING_LABEL[m.reason]).join("; ")].map(esc).join(","));
    }
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `missing-${typeIds.map(typeName).join("-").replace(/[^a-z0-9]+/gi, "_").slice(0, 60) || "credentials"}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="rounded-lg border bg-muted/20">
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left">
        <span>
          <span className="block text-sm font-semibold">Who is missing a credential?</span>
          <span className="block text-xs text-muted-foreground">Pick a credential, narrow by age, and see who does not have it: for example workers under 25 without WHMIS.</span>
        </span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="space-y-4 border-t px-4 py-4">
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Credential(s)</Label>
            {typeIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {typeIds.map((id) => (
                  <button key={id} type="button" onClick={() => toggleType(id)} className="inline-flex items-center gap-1 rounded-full border border-paris-red bg-paris-red/10 px-2.5 py-1 text-xs text-paris-red-text" aria-label={`Remove ${typeName(id)}`}>
                    {typeName(id) || "Credential"} <X className="h-3 w-3" />
                  </button>
                ))}
              </div>
            )}
            <PickerSearch value={typeQuery} onChange={setTypeQuery} placeholder="Type to search credentials…" />
            <div className="max-h-40 divide-y overflow-y-auto rounded-lg border bg-background">
              {types.filter((t) => matchesQuery(typeQuery, t.name)).map((t) => (
                <button key={t.id} type="button" onClick={() => toggleType(t.id)} className={cn("flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors", typeIds.includes(t.id) ? "bg-paris-red/8" : "hover:bg-muted/40")}>
                  <span className="truncate">{t.name}</span>
                  {typeIds.includes(t.id) && <Check className="ml-2 h-4 w-4 shrink-0 text-paris-red" />}
                </button>
              ))}
              {types.length === 0 && <p className="px-3 py-3 text-center text-sm text-muted-foreground">No credentials yet.</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Who to check</Label>
            <div className="flex flex-wrap items-center gap-2">
              {([["crew", "Paris crew"], ["subs", "Subcontractors"], ["both", "Both"]] as const).map(([k, label]) => (
                <button key={k} type="button" onClick={() => setWho(k)} className={cn("rounded-full border px-3 py-1 text-xs transition-colors", who === k ? "border-paris-red bg-paris-red/10 text-paris-red-text" : "hover:bg-muted")}>{label}</button>
              ))}
              {divisions.length > 0 && who !== "subs" && (
                <div className="w-44">
                  <SearchableSelect size="sm" value={divisionId} onChange={setDivisionId} allLabel="All divisions" allValue="" options={divisions.map((d) => ({ value: d.id, label: d.name }))} />
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Age</Label>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label htmlFor="miss-age-min" className="text-[11px] text-muted-foreground">From (years)</Label>
                <Input id="miss-age-min" type="number" min={0} max={99} inputMode="numeric" value={ageMin} onChange={(e) => setAgeMin(e.target.value)} className="h-8 w-24 text-sm" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="miss-age-max" className="text-[11px] text-muted-foreground">To (years)</Label>
                <Input id="miss-age-max" type="number" min={0} max={99} inputMode="numeric" value={ageMax} onChange={(e) => setAgeMax(e.target.value)} className="h-8 w-24 text-sm" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="miss-born-from" className="text-[11px] text-muted-foreground">Born on or after</Label>
                <input id="miss-born-from" type="date" max={bornTo || undefined} value={bornFrom} onChange={(e) => setBornFrom(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-sm" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="miss-born-to" className="text-[11px] text-muted-foreground">Born on or before</Label>
                <input id="miss-born-to" type="date" min={bornFrom || undefined} value={bornTo} onChange={(e) => setBornTo(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-sm" />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {agePresets.map((a) => (
                <button key={a.label} type="button" onClick={() => { setAgeMin(a.min); setAgeMax(a.max); setBornFrom(""); setBornTo(""); }} className={cn("rounded-full border px-2.5 py-1 text-xs transition-colors", ageMin === a.min && ageMax === a.max && !bornFrom && !bornTo ? "border-paris-red bg-paris-red/10 text-paris-red-text" : "hover:bg-muted")}>{a.label}</button>
              ))}
              {filtersOn && (
                <button type="button" className="rounded-full px-2.5 py-1 text-xs text-muted-foreground underline underline-offset-2" onClick={() => { setDivisionId(""); setAgeMin(""); setAgeMax(""); setBornFrom(""); setBornTo(""); }}>Clear filters</button>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">With an age or birth date chosen, people with no date of birth on file are left out.</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">What counts as missing</Label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4" checked={countExpired} onChange={(e) => setCountExpired(e.target.checked)} /> Expired credentials</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4" checked={countSoon} onChange={(e) => setCountSoon(e.target.checked)} /> Credentials expiring soon</label>
            <p className="text-[11px] text-muted-foreground">No record and revoked always count.</p>
          </div>

          {typeIds.length === 0 ? (
            <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">Pick a credential above to see who is missing it.</p>
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium">
                  {isFetching && !result ? "Checking…" : `${people.length} ${people.length === 1 ? "person is" : "people are"} missing it`}
                  {result ? <span className="font-normal text-muted-foreground"> · {result.checked} checked</span> : null}
                </p>
                {people.length > 0 && (
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={exportCsv}>Export list</Button>
                    {typeIds.length === 1 && (
                      <Button type="button" size="sm" className="h-8 text-xs" onClick={() => setAssignOpen(true)}>Assign it to {people.length === 1 ? "them" : `these ${people.length}`}</Button>
                    )}
                  </div>
                )}
              </div>
              {people.length > 0 && (
                <ul className="max-h-96 divide-y overflow-y-auto rounded-lg border bg-background">
                  {people.map((p) => (
                    <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span className="min-w-0">
                        <span className="font-medium">{p.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {[p.age !== null ? `${p.age} yrs` : null, p.isSub ? "Subcontractor" : p.division, p.jobTitle].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                      <span className="flex flex-wrap justify-end gap-1">
                        {p.missing.map((m) => (
                          <span key={m.certTypeId} className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", MISSING_CLASS[m.reason])} title={m.expiryDate ? `Expiry date ${m.expiryDate}` : undefined}>
                            {typeIds.length > 1 ? `${m.name}: ` : ""}{MISSING_LABEL[m.reason]}
                          </span>
                        ))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {assignOpen && (
        <CreateCertAssignmentSheet
          prefill={{ certTypeId: typeIds[0] ?? null, workerIds: people.map((p) => p.id) }}
          onClose={() => setAssignOpen(false)}
          onCreated={() => {
            setAssignOpen(false);
            queryClient.invalidateQueries({ queryKey: ["admin", "cert-assignments"] });
            queryClient.invalidateQueries({ queryKey: ["admin", "cert-missing"] });
          }}
        />
      )}
    </div>
  );
}

/** One card per credential: how many are valid, expiring, expired; "Who has it" lists the people. */
function ComplianceCards({ entries, subs = false }: { entries: ComplianceEntry[]; subs?: boolean }) {
  const { data: profileSession } = authClient.useSession();
  const canOpenProfile = ["admin", "div_admin"].includes(profileSession?.user?.role ?? "");
  const [openTypeId, setOpenTypeId] = useState<string | null>(null);
  return (
    <div className="space-y-3">
      {entries.map((entry) => (
        <Card key={entry.groupKey}>
          <CardContent className="py-3 px-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{entry.certTypeName}</span>
                {entry.isCustom ? (
                  <Badge variant="secondary" className="text-xs px-1.5 py-0">Custom</Badge>
                ) : null}
                {entry.isRequired && (
                  <Badge className="bg-blue-500/15 text-blue-700 dark:text-blue-400 text-xs px-1.5 py-0">
                    Required
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {entry.total} total
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 px-2 text-xs"
                  onClick={() => setOpenTypeId(openTypeId === entry.groupKey ? null : entry.groupKey)}
                  aria-expanded={openTypeId === entry.groupKey}
                >
                  <Users className="h-3.5 w-3.5" />
                  {openTypeId === entry.groupKey ? "Hide" : "Who has it"}
                </Button>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Badge className="bg-green-500/15 text-green-700 dark:text-green-400 text-xs gap-0.5">
                <CheckCircle className="h-3 w-3" />
                {entry.valid} valid
              </Badge>
              {entry.expiringSoon > 0 && (
                <Badge className="bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 text-xs gap-0.5">
                  <AlertTriangle className="h-3 w-3" />
                  {entry.expiringSoon} expiring
                </Badge>
              )}
              {entry.expired > 0 && (
                <Badge className="bg-red-500/15 text-red-700 dark:text-red-400 text-xs gap-0.5">
                  <XCircle className="h-3 w-3" />
                  {entry.expired} expired
                </Badge>
              )}
              {entry.revoked > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {entry.revoked} revoked
                </Badge>
              )}
            </div>
            {openTypeId === entry.groupKey && (
              subs ? (
                <SubCertHolders certTypeId={entry.certTypeId} customName={entry.certTypeName} />
              ) : (
                <CertHolders certTypeId={entry.certTypeId} customName={entry.certTypeName} canOpenProfile={canOpenProfile} />
              )
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
